//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback, useState } from 'react';
import { useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
// import { useLocation } from 'react-router-dom';
import SimpleBar from 'simplebar-react';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import useSimpleBarRef from '../common/hooks/useSimpleBarRef';
import { AFITD_DOMAIN } from '../system/constants';
import { sendResetPasswordEmail } from '../common/services/authServices';
import { AUTH_ERROR_CODE_USER_NOT_FOUND, AUTH_ERROR_MSG_DEFAULT } from '../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import TopNav from '../topNav/TopNav';
import AfitdTextField from '../common/elemental/AfitdTextField';
import AfitdTypography from '../common/elemental/AfitdTypography';
import AfitdButton from '../common/elemental/AfitdButton';
import PageMessage from '../common/elemental/PageMessage';
import AfitdMessageDialog from '../common/elemental/AfitdMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: { backgroundColor: '#FAFAFC', overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    pageHeader: {
        maxHeight: '81px',
        // [theme.breakpoints.down('md')]: { maxHeight: '134px' }, // commented out because no more second row menu -el 20221022
        // [theme.breakpoints.down('sm')]: { maxHeight: '81px' },
    },
    pageContent: {
        flex: 1,
        overflowY: 'hidden',
    },
    tempContainer: { alignItems: 'center', marginTop: 50, paddingLeft: 16, paddingRight: 16 },
    instructionText: { marginBottom: 10 },
    email: { marginBottom: 10, maxWidth: 500 },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        email,
        handleChangeEmail,
        errorMessage,
        handleCloseErrorMessageDialog,
        handleClickSubmitButton,
    } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true },
        tempContainer: { item: true, container: true, direction: 'column' },
        forgotPasswordForm: {
            email,
            emailOnChange: handleChangeEmail,
            onSubmit: handleClickSubmitButton,
        },
        errorMessageDialog: {
            title: 'Error',
            open: !!errorMessage,
            message: errorMessage,
            onClose: handleCloseErrorMessageDialog,
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const PAGE_ID_FORGOT_PASSWORD_FORM = 1;
const PAGE_ID_RESET_EMAIL_SENT_SUCCESSFUL = 2;

const ForgotPasswordForm = props => {
    const { email, emailOnChange, onSubmit } = props;

    const classes = useStyles();
    const defns = getDefns({ classes, props });

    return (
        <>
            <AfitdTypography {...defns.instructionText}>Forgot your password? Enter the email you have used to register with us and we'll send you a reset link.</AfitdTypography>
            <AfitdTextField {...defns.email} placeholder={`john@${AFITD_DOMAIN}`} value={email} onChange={emailOnChange} />
            <AfitdButton {...defns.submitButton} color={'secondary'} onClick={onSubmit}>
                Submit
            </AfitdButton>
        </>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ForgotPasswordLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    // const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    // const { setLoadingDialogOpen } = context;
    const { simpleBarRef: pageContentSimpleBarEl, scrollToTop: scrollToPageTop } = useSimpleBarRef();

    // const location = useLocation();
    // const subPageId = _get(location, 'state.subPageId');

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [currentPage, setCurrentPage] = useState(PAGE_ID_FORGOT_PASSWORD_FORM);
    const [email, setEmail] = useState();
    const [errorMessage, setErrorMessage] = useState('');

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const getPageComponent = defns => {
        switch (currentPage) {
            case PAGE_ID_RESET_EMAIL_SENT_SUCCESSFUL:
                return <PageMessage resetEmailSentSuccessful />;
            case PAGE_ID_FORGOT_PASSWORD_FORM:
                return <ForgotPasswordForm {...defns.forgotPasswordForm} />;
            default:
                return null;
        }
    };

    const handleChangeEmail = (evt, a) => {
        setEmail(evt.target.value);
    };

    const handleClickSubmitButton = async () => {
        try {
            await sendResetPasswordEmail({ email });
            // console.log('>>>>>>>>>>>>>> sent reset-password email successfully', resp);
            setCurrentPage(PAGE_ID_RESET_EMAIL_SENT_SUCCESSFUL);
        } catch (authError) {
            const code = _get(authError, 'code');
            if (code === AUTH_ERROR_CODE_USER_NOT_FOUND) {
                setCurrentPage(PAGE_ID_RESET_EMAIL_SENT_SUCCESSFUL);
            } else {
                setErrorMessage(_get(authError, 'displayMessage', AUTH_ERROR_MSG_DEFAULT));
            }
        }
    };

    const handleCloseErrorMessageDialog = useCallback(() => {
        setErrorMessage('');
    }, [setErrorMessage]);

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, email, handleChangeEmail, errorMessage, handleCloseErrorMessageDialog, handleClickSubmitButton });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, scrollToPageTop }}>
            <Grid {...defns.pageContainer}>
                <Grid {...defns.pageHeader}>
                    <TopNav />
                </Grid>
                <Grid {...defns.pageContent}>
                    <SimpleBar style={{ height: '100%', width: '100%', flex: 1 }} ref={pageContentSimpleBarEl}>
                        <Grid {...defns.tempContainer}>{getPageComponent(defns)}</Grid>
                    </SimpleBar>
                </Grid>
            </Grid>
            {!!errorMessage && <AfitdMessageDialog {...defns.errorMessageDialog} />}
        </LayoutContext.Provider>
    );
};

export default ForgotPasswordLayout;
